import { canViewerSeeCareRecord } from '../../services/timeline/recordVisibility';
import { buildCaptureDestinationReceipt } from './captureDestinationReceiptModel';
import {
  buildManualReviewQueueSummary,
  groupCareSourcesByCapture,
} from './profileReviewQueueModel';
import { getCareSourceContext, getCareSourceRecordedAtMillis } from './careSourceStatusModel';
import { getMedicationSourceSuggestions } from './medicationSourceSuggestions';

const CONFIRMATION_DESTINATIONS = new Set(['Goals', 'Medications', 'Allergies']);

const toLocalDateKey = (millis = 0) => {
  if (!millis) return '';
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const getConfirmationSummary = (sources = [], structuredDestinationsBySource) => {
  const summary = buildManualReviewQueueSummary(sources, structuredDestinationsBySource);
  const destinations = Object.fromEntries(
    Object.entries(summary.destinations)
      .filter(([label, count]) => CONFIRMATION_DESTINATIONS.has(label) && Number(count) > 0)
  );

  return {
    destinations,
    pendingTotal: Object.values(destinations).reduce((total, count) => total + Number(count || 0), 0),
  };
};

const getLatestCaptureMillis = (sources = []) => sources.reduce(
  (latest, source) => Math.max(latest, getCareSourceRecordedAtMillis(source)),
  0
);

export const buildProfileUpdateTimelineReceipts = (
  sources = [],
  viewerRole = null,
  structuredDestinationsBySource
) => (
  groupCareSourcesByCapture(
    sources.filter((source) => canViewerSeeCareRecord({ viewerRole, record: source }))
  )
    .map((group) => {
      const destinationReceipt = buildCaptureDestinationReceipt(group.sources);
      const confirmation = getConfirmationSummary(group.sources, structuredDestinationsBySource);
      const capturedAtMillis = getLatestCaptureMillis(group.sources);
      const savedDetailCount = destinationReceipt.profileSections.reduce(
        (total, section) => total + section.items.length,
        0
      );
      const medicationSources = group.sources.filter(
        (source) => getCareSourceContext(source) === 'medication'
      );
      const medicationReviewCount = medicationSources.reduce(
        (total, source) => total + getMedicationSourceSuggestions(source).length,
        0
      );

      return {
        id: `profile-update:${group.key}`,
        sourceKey: group.key,
        sources: group.sources,
        capturedAtMillis,
        dateKey: toLocalDateKey(capturedAtMillis),
        profileSections: destinationReceipt.profileSections,
        savedDetailCount,
        isMedicationSource: medicationSources.length > 0,
        medicationReviewCount,
        pendingTotal: confirmation.pendingTotal,
        pendingDestinations: confirmation.destinations,
      };
    })
    .filter((receipt) => receipt.savedDetailCount > 0 || receipt.pendingTotal > 0 || receipt.medicationReviewCount > 0)
    .sort((left, right) => right.capturedAtMillis - left.capturedAtMillis)
);

export const buildProfileReceiptCalendarEntries = (receipts = []) => (
  receipts.map((receipt) => ({
    id: receipt.id,
    collection: 'careSources',
    type: 'profile_update_receipt',
    timelineType: 'profile_update_receipt',
    category: 'profile_update_receipt',
    entryDayKey: receipt.dateKey,
    localDate: receipt.dateKey,
    timestamp: receipt.capturedAtMillis ? new Date(receipt.capturedAtMillis) : null,
    isProfileUpdateReceipt: true,
  }))
);

export const getProfileUpdateReceiptTitle = (receipt = {}, personName = 'Profile') => (
  receipt.savedDetailCount > 0
    ? `${personName}'s profile was updated`
    : 'Note added to Lifelog'
);

export const getProfileUpdateReceiptSummary = (receipt = {}) => {
  const savedCount = Number(receipt.savedDetailCount || 0);
  const pendingCount = Number(receipt.pendingTotal || 0);
  if (savedCount && pendingCount) {
    return `${savedCount} detail${savedCount === 1 ? '' : 's'} added · ${pendingCount} need${pendingCount === 1 ? 's' : ''} review`;
  }
  if (savedCount) {
    return `${savedCount} detail${savedCount === 1 ? '' : 's'} added`;
  }
  return pendingCount > 0 ? 'Profile details need review' : 'Profile details organized';
};

export { toLocalDateKey };
